import { useEffect, useMemo, useState } from "react";import axios from "axios";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal } from "./Reveal";
import { useCart } from "../context/CartContext";
import { API } from "../data/content";

const FILTERS = ["All", "Cheeseboards & Grazing", "Braai & Social", "Platters & Finger Foods", "Weekly Meals & Juices", "Muffins", "Scones", "Cupcakes", "Pantry", "Gift & Occasion Boxes"];

const DAY_STARCHES = ["Rice", "Pap", "Pasta", "Roast Potatoes", "Sweet Potato Mash", "Butternut Mash", "Dumpling"];
const DAY_SIDES = ["Roasted Veggies", "Creamed Spinach", "Butternut", "Coleslaw", "Potato Salad", "Roasted Potatoes", "Green Salad", "Chakalaka", "Salsa"];

const daySelect = "bg-[#0A0A0A] border border-[#F9F9F6]/20 focus:border-[#D4AF37] px-2 py-1.5 text-[11px] text-[#F9F9F6] rounded transition-colors [color-scheme:dark]";

function ProductCard({ p, index }) {
  const { add } = useCart();
  const [selected, setSelected] = useState(p.options?.[0]?.label ?? null);
  const [flavour, setFlavour] = useState(p.flavours?.[0] ?? null);
  const [size, setSize] = useState(p.sizes?.[0]?.label ?? null);
  const [groupSel, setGroupSel] = useState(() => p.groups ? Object.fromEntries(p.groups.map((g) => [g.name, g.labels[0]])) : null);
  const [days, setDays] = useState({});
  const isCombo = !!(p.flavours && p.sizes);
  const isGroups = !!p.groups;
  const isPerDay = !!p.per_day;
  const planLabel = isGroups ? groupSel["Plan"] : null;
  const planMatch = planLabel?.match(/^(\d+) Meals/);
  const dayCount = planMatch ? parseInt(planMatch[1], 10) : 0;

  useEffect(() => {
    setDays((prev) => {
      const next = {};
      for (let d = 1; d <= dayCount; d++) {
        next[d] = prev[d] || { starch: DAY_STARCHES[0], side: DAY_SIDES[0] };
      }
      return next;
    });
  }, [dayCount]);

  const daySelections = isPerDay && dayCount > 0
    ? Array.from({ length: dayCount }, (_, i) => {
        const d = days[i + 1] || { starch: DAY_STARCHES[0], side: DAY_SIDES[0] };
        return `Day ${i + 1}: ${d.starch} + ${d.side}`;
      })
    : null;
  const comboLabel = isCombo ? `${flavour} · ${size}` : (isGroups ? p.groups.map((g) => groupSel[g.name]).join(" · ") : null);
  const activeOption = (isCombo || isGroups)
    ? p.options.find((o) => o.label === comboLabel)
    : (p.options?.find((o) => o.label === selected) ?? null);
  const price = activeOption?.price ?? p.price;

  const pill = (activeState) =>
    `text-[9px] uppercase tracking-[0.1em] rounded-full px-2.5 py-1 border transition-colors ${activeState ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold" : "border-[#F9F9F6]/20 text-[#F9F9F6]/60 hover:border-[#D4AF37]/60"}`;

  return (
    <motion.article data-testid={`product-card-${p.id}`}
                    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: (index % 4) * 0.08 }}
                    className="group border border-[#F9F9F6]/10 hover:border-[#D4AF37]/50 transition-colors bg-[#1A1A1A] flex flex-col">
      <div className="relative aspect-[4/5] overflow-hidden vignette">
        <img src={p.image} alt={p.name} loading="lazy"
             className="w-full h-full object-cover scale-100 group-hover:scale-105 transition-transform duration-700" />
        {p.image_2 && (
          <img src={p.image_2} alt={`${p.name} — packaged`} loading="lazy"
               className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        )}
        <span className="absolute top-3 left-3 z-10 text-[9px] uppercase tracking-[0.2em] bg-[#0A0A0A]/80 backdrop-blur px-3 py-1.5 text-[#D4AF37] border border-[#D4AF37]/30">
          {p.category}
        </span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-serif-e text-xl text-[#F9F9F6] leading-snug">{p.name}</h3>
        <p className="text-[#F9F9F6]/50 text-xs leading-relaxed mt-2 line-clamp-2">{p.description}</p>
        {isCombo && (
          <>
            <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`options-${p.id}`}>
              {p.flavours.map((f) => (
                <button key={f} onClick={() => setFlavour(f)}
                        data-testid={`option-${p.id}-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={pill(flavour === f)}>
                  {f}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`sizes-${p.id}`}>
              {p.sizes.map((s) => (
                <button key={s.label} onClick={() => setSize(s.label)}
                        data-testid={`size-${p.id}-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={pill(size === s.label)}>
                  {s.label}
                </button>
              ))}
            </div>
          </>
        )}
        {isGroups && p.groups.map((g) => (
          <div key={g.name} className="mt-3" data-testid={`group-${p.id}-${g.name.toLowerCase()}`}>
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#F9F9F6]/40 mb-1.5">{g.name}</p>
            <div className="flex flex-wrap gap-1.5">
              {g.labels.map((l) => (
                <button key={l} onClick={() => setGroupSel({ ...groupSel, [g.name]: l })}
                        data-testid={`group-option-${p.id}-${g.name.toLowerCase()}-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={pill(groupSel[g.name] === l)}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        ))}
        {!isCombo && !isGroups && p.options && (
          <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`options-${p.id}`}>
            {p.options.map((o) => (
              <button key={o.label} onClick={() => setSelected(o.label)}
                      data-testid={`option-${p.id}-${o.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={pill(selected === o.label)}>
                {o.label}
              </button>
            ))}
          </div>
        )}
        {isPerDay && dayCount > 0 && (
          <div className="mt-3 space-y-2" data-testid={`days-${p.id}`}>
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#F9F9F6]/40">Choose starch &amp; side per day</p>
            {Array.from({ length: dayCount }, (_, i) => {
              const d = days[i + 1] || { starch: DAY_STARCHES[0], side: DAY_SIDES[0] };
              return (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-[10px] text-[#D4AF37] w-9 shrink-0">Day {i + 1}</span>
                  <select value={d.starch} data-testid={`day-${i + 1}-starch-${p.id}`}
                          onChange={(e) => setDays({ ...days, [i + 1]: { ...d, starch: e.target.value } })}
                          className={daySelect}>
                    {DAY_STARCHES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select value={d.side} data-testid={`day-${i + 1}-side-${p.id}`}
                          onChange={(e) => setDays({ ...days, [i + 1]: { ...d, side: e.target.value } })}
                          className={daySelect}>
                    {DAY_SIDES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex items-center justify-between mt-4 pt-1 mt-auto">
          <div>
            <p data-testid={`product-price-${p.id}`} className="text-[#D4AF37] font-semibold">
              {p.options && !activeOption ? `from R${p.price.toLocaleString()}` : `R${price.toLocaleString()}`}
            </p>
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#F9F9F6]/40">{activeOption ? activeOption.label : p.serves}</p>
          </div>
          <button onClick={() => add(p, activeOption, daySelections)} data-testid={`add-to-cart-${p.id}`} aria-label={`Add ${p.name} to cart`}
                  className="w-10 h-10 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors">
            <Plus size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </motion.article>
  );
}

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const visible = useMemo(() => filter === "All" ? products : products.filter((p) => p.category === filter), [products, filter]);

  return (
    <section id="shop" data-testid="shop-section" className="noise relative bg-[#0A0A0A] py-28 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">The Novaphi Shop</p>
          <h2 className="font-serif-e text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#F9F9F6] max-w-3xl">
            Order the experience.
          </h2>
          <p className="text-[#F9F9F6]/60 max-w-xl mt-5 text-base leading-relaxed">
            Cheeseboards, braai packs, platters, muffins, scones, juices and pantry favourites — add to your cart and check out via WhatsApp. Weekly meal prep orders close on Sundays; an 80% deposit secures your slot. Collection in Johannesburg, Uber/Bolt delivery, and Bobgo courier for pantry items.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex flex-wrap gap-3 mt-12 mb-12" data-testid="shop-filters">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`rounded-full px-5 py-2 text-[11px] uppercase tracking-[0.15em] border transition-colors ${filter === f ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold" : "border-[#F9F9F6]/20 text-[#F9F9F6]/60 hover:border-[#D4AF37]/60 hover:text-[#F9F9F6]"}`}>
                {f}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {visible.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
        </div>
      </div>
    </section>
  );
}
